import { Builder } from '@builder.io/react'
import { Cache } from 'aws-amplify'
import dayjs from 'dayjs'
import { parse } from 'query-string'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updateCouponCodeValidity } from 'state/actions/dogAction'
import LinkButton, { LinkButtonProp, LinkButtonRegisterProps } from '../common/button'

enum Position {
  Bottom = 'bottom',
  Top = 'top'
}

interface Props {
  bannerText: string
  bannerTextMobile: string
  backgroundColor: string
  fontColor: string
  stickyBottom: boolean
  bannerPosition: Position
  buttonProps: LinkButtonProp
  centered: boolean
  leftIcon: any
  rightIcon: any
  defaultOffer: boolean
}
const BannerWithIcons = ({
  bannerText,
  bannerTextMobile,
  backgroundColor,
  fontColor,
  stickyBottom,
  bannerPosition,
  buttonProps,
  centered,
  leftIcon,
  rightIcon,
  defaultOffer
}: Props) => {
  const [couponMessage, setCouponMessage] = useState('')

  const {
    isValidCoupon,
    lastValidatedCoupons
  }: {
    isValidCoupon: boolean
    lastValidatedCoupons: CouponsType[]
    loading: boolean
  } = useSelector((state: RootState) => state.dog)
  const dispatch = useDispatch()

  // Handle coupons from the URL querystring
  useEffect(() => {
    // Priorise the QS sign up flow coupon, then look for a content page coupon, otherwise we fallback to the default
    if (!defaultOffer) {
      const { coupon: couponFromQueryStr } = parse(location.search)
      let coupon = couponFromQueryStr ?? Cache.getItem('coupon') ?? null
      if (coupon) {
        coupon = coupon.toUpperCase()
        dispatch(updateCouponCodeValidity('checkValidity', coupon as string))

        if (isValidCoupon) {
          const couponMessageText = getCouponMessage()
          setCouponMessage(couponMessageText)
        }
      }
    }
  }, [])

  useEffect(() => {
    if (lastValidatedCoupons?.length) setCouponMessage(getCouponMessage())
  }, [lastValidatedCoupons])

  // Create dynamic coupon message
  const getCouponMessage = () => {
    return `Free Treat & ${
      lastValidatedCoupons[0]?.type === 'percentage'
        ? lastValidatedCoupons[0].amount + '%'
        : '£' + (lastValidatedCoupons[0].amount / 100).toFixed(2)
    } off ${lastValidatedCoupons[0].uses > 1 ? `your first ${lastValidatedCoupons[0].uses} boxes!` : 'your first box!'}`
  }

  const StickyWrapper = ({ children, position }: { children: any; position: Position }) => {
    const bannerPositionClass = position === Position.Top ? 'top-0' : 'bottom-0'

    return <div className={`fixed inset-x-0 ${bannerPositionClass}`}>{children}</div>
  }

  const InnerContent = ({ buttonProps, centered, bannerText, bannerTextMobile, fontColor }: any) => {
    if (centered) {
      return (
        <>
          <div className="flex items-center justify-center sm:px-16">
            {leftIcon && (
              <img
                className="mx-2 h-4 w-4 items-center lg:mr-4 lg:h-8 lg:w-8"
                src={leftIcon}
                alt="Megaphone"
                loading="lazy"
              />
            )}
            {/* Desktop banner text */}
            <p
              className={'text-sm font-medium sm:text-base md:mr-5 md:mb-0 hidden lg:block'}
              style={{ color: fontColor }}
              dangerouslySetInnerHTML={{ __html: couponMessage ? couponMessage : bannerText }}
            ></p>
            {/* Mobile banner text */}
            <a href={buttonProps.buttonUrl}>
              {couponMessage && (
                <p
                  className={'text-sm font-medium sm:text-base md:mr-5 md:mb-0 block lg:hidden'}
                  style={{ color: fontColor }}
                  dangerouslySetInnerHTML={{ __html: couponMessage ? couponMessage : bannerText }}
                ></p>
              )}
              {!couponMessage && (
                <p
                  className={'text-sm font-medium sm:text-base md:mr-5 md:mb-0 block lg:hidden'}
                  style={{ color: fontColor }}
                  dangerouslySetInnerHTML={{ __html: bannerTextMobile ? bannerTextMobile : bannerText }}
                ></p>
              )}
            </a>
            {rightIcon && (
              <img
                className="mx-2 h-4 w-4 items-center lg:mr-4 lg:h-8 lg:w-8 block sm:hidden"
                src={rightIcon}
                alt="Megaphone"
                loading="lazy"
              />
            )}
            <div className="hidden pl-4 sm:block">
              <LinkButton
                buttonColor={buttonProps.buttonColor}
                buttonText={buttonProps.buttonText}
                loggedInButtonText={buttonProps.loggedInButtonText}
                buttonTextColor={buttonProps.buttonTextColor}
                buttonUrl={buttonProps.buttonUrl}
                isLink={buttonProps.isLink}
                onClick={() => {
                  if (typeof window.gtag !== 'undefined') {
                    window.gtag('event', 'cta_button_click', {
                      page: 'homepage'
                    })
                  }
                }}
              />
            </div>
          </div>
        </>
      )
    }

    return (
      <div className="flex flex-wrap items-center justify-between">
        <div className="flex w-0 flex-1 items-center justify-center sm:justify-start">
          {leftIcon && (
            <img
              className="mx-2 h-4 w-4 items-center lg:mr-4 lg:h-8 lg:w-8"
              src={leftIcon}
              alt="Megaphone"
              loading="lazy"
            />
          )}
          {/* Desktop banner text */}
          <p
            className={'enable-list-styles text-base text-center font-medium font-sans hidden lg:block'}
            style={{ color: fontColor }}
            dangerouslySetInnerHTML={{ __html: bannerText }}
          ></p>
          {/* Mobile banner text */}
          <p
            className={'enable-list-styles text-base text-center font-medium font-sans block lg:hidden'}
            style={{ color: fontColor }}
            dangerouslySetInnerHTML={{ __html: bannerTextMobile ? bannerTextMobile : bannerText }}
          ></p>
          {rightIcon && (
            <img
              className="mx-2 h-4 w-4 items-center lg:mr-4 lg:h-8 lg:w-8"
              src={rightIcon}
              alt="Megaphone"
              loading="lazy"
            />
          )}
        </div>
        <div className="order-3 mt-2 hidden w-full flex-shrink-0 sm:order-2 sm:mt-0 sm:block sm:w-auto">
          <LinkButton
            buttonColor={buttonProps.buttonColor}
            buttonText={buttonProps.buttonText}
            loggedInButtonText={buttonProps.loggedInButtonText}
            buttonTextColor={buttonProps.buttonTextColor}
            buttonUrl={buttonProps.buttonUrl}
            isLink={buttonProps.isLink}
            onClick={() => {
              if (typeof window.gtag !== 'undefined') {
                window.gtag('event', 'cta_button_click', {
                  page: 'homepage'
                })
              }
            }}
          />
        </div>
      </div>
    )
  }

  const Content = ({ bannerText, bannerTextMobile, buttonProps, centered, backgroundColor, fontColor }: any) => {
    return (
      <div
        className="relative bg-indigo-600"
        style={{ backgroundColor: backgroundColor, color: fontColor, zIndex: '999' }}
      >
        <div className="mx-auto max-w-7xl py-3 px-3 sm:px-6 lg:px-8">
          <InnerContent
            bannerText={bannerText}
            bannerTextMobile={bannerTextMobile}
            buttonProps={buttonProps}
            centered={centered}
            fontColor={fontColor}
          />
        </div>
      </div>
    )
  }

  if (stickyBottom) {
    return (
      <StickyWrapper position={bannerPosition}>
        <Content
          bannerText={bannerText}
          bannerTextMobile={bannerTextMobile}
          buttonProps={buttonProps}
          centered={centered}
          backgroundColor={backgroundColor}
          fontColor={fontColor}
        />
      </StickyWrapper>
    )
  }

  return (
    <Content
      bannerText={bannerText}
      bannerTextMobile={bannerTextMobile}
      buttonProps={buttonProps}
      centered={centered}
      backgroundColor={backgroundColor}
      fontColor={fontColor}
    />
  )
}
export default BannerWithIcons
Builder.registerComponent(BannerWithIcons, {
  name: 'BannerWithIcons',
  inputs: [
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'green'
    },
    {
      name: 'bannerPosition',
      type: 'string',
      defaultValue: 'top',
      enum: ['bottom', 'top']
    },
    {
      name: 'bannerText',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'bannerTextMobile',
      type: 'richText',
      defaultValue: ''
    },
    {
      name: 'fontColor',
      type: 'color',
      defaultValue: 'black'
    },
    {
      name: 'centered',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'stickyBottom',
      type: 'boolean',
      defaultValue: false
    },
    {
      name: 'leftIcon',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      name: 'rightIcon',
      type: 'file',
      allowedFileTypes: ['jpeg', 'png', 'svg']
    },
    {
      ...LinkButtonRegisterProps
    },
    {
      name: 'defaultOffer',
      type: 'boolean',
      defaultValue: false
    }
  ]
})
